import { translate } from '../../../../../common/intl';
import {
  HelpAgentRemoteElement,
  HelpAgentRemoteFooterLinks,
} from './helpAgentRemote.types';
import { HelpAgentRemoteLoader } from './helpAgentRemoteLoader';

interface HelpAgentHostElements {
  mount: HTMLElement
  status: HTMLElement
}

interface HelpAgentHostStatusShell {
  root: HTMLElement
  loading: HTMLElement
  error: HTMLElement
  ring: HTMLElement
}

interface HelpAgentHostCallbacks {
  close: () => void
  reposition: () => void
  logout: () => void
}

interface HelpAgentHostAdapterOptions {
  loader: Pick<HelpAgentRemoteLoader, 'load' | 'retry'>
  elements: HelpAgentHostElements
  footerLinks: HelpAgentRemoteFooterLinks
  quickPrompts?: readonly string[]
  notificationsEnabled?: boolean
  callbacks: HelpAgentHostCallbacks
}

export class HelpAgentHostAdapter {
  private desiredOpen = false;
  private lifecycleGeneration = 0;
  private loading: Promise<void> | null = null;
  private shouldFocusAfterLoad = false;
  private statusShell: HelpAgentHostStatusShell | null = null;
  private widget: HelpAgentRemoteElement | null = null;

  public constructor (private readonly options: HelpAgentHostAdapterOptions) {
    this.options.elements.status.setAttribute('role', 'status');
    this.options.elements.status.setAttribute('aria-live', 'polite');
  }

  public open (): Promise<void> {
    this.desiredOpen = true;
    return this.load(() => this.options.loader.load());
  }

  public close (): void {
    this.desiredOpen = false;
    this.shouldFocusAfterLoad = false;
  }

  public retry (): Promise<void> {
    return this.load(() => this.options.loader.retry());
  }

  public focusPanelContent (): void {
    if (this.widget && this.desiredOpen) {
      this.widget.focusInput();
      return;
    }
    if (!this.widget) {
      this.shouldFocusAfterLoad = this.desiredOpen;
    }
  }

  public disconnect (): void {
    this.desiredOpen = false;
    this.shouldFocusAfterLoad = false;
    this.invalidateLifecycle();
    this.clearStatus();
  }

  private load (load: () => Promise<void>): Promise<void> {
    if (this.widget) {
      return Promise.resolve();
    }
    if (this.loading) {
      return this.loading;
    }

    this.showLoading();
    const generation = this.lifecycleGeneration;
    const loading = load().then(
      () => {
        if (generation !== this.lifecycleGeneration) {
          return;
        }
        if (!this.desiredOpen) {
          this.clearStatus();
          return;
        }
        this.createWidget();
        if (this.shouldFocusAfterLoad && this.desiredOpen) {
          this.shouldFocusAfterLoad = false;
          this.widget?.focusInput();
        }
        this.clearStatus();
      },
      (error: unknown) => {
        if (generation === this.lifecycleGeneration) {
          this.showFailure();
        }
        if (this.loading === loading) {
          this.loading = null;
        }
        throw error;
      },
    ).finally(() => {
      if (this.loading === loading) {
        this.loading = null;
      }
    });
    this.loading = loading;
    return loading;
  }

  private invalidateLifecycle (): void {
    this.lifecycleGeneration += 1;
    this.loading = null;
    this.disposeWidget();
  }

  private createWidget (): void {
    if (this.widget) {
      return;
    }
    const widget = document.createElement('help-agent-app');
    widget.quickPrompts = this.options.quickPrompts ?? [];
    widget.footerLinks = this.options.footerLinks;
    widget.notificationsEnabled = this.options.notificationsEnabled ?? true;
    widget.addEventListener('help-agent:close', this.onClose);
    widget.addEventListener('help-agent:expanded-change', this.onExpandedChange);
    widget.addEventListener('help-agent:notify', this.onNotify);
    widget.addEventListener('help-agent:auth-error', this.onAuthError);
    this.options.elements.mount.append(widget);
    this.widget = widget;
    this.options.callbacks.reposition();
  }

  private disposeWidget (): void {
    if (!this.widget) {
      return;
    }
    this.widget.removeEventListener('help-agent:close', this.onClose);
    this.widget.removeEventListener('help-agent:expanded-change', this.onExpandedChange);
    this.widget.removeEventListener('help-agent:notify', this.onNotify);
    this.widget.removeEventListener('help-agent:auth-error', this.onAuthError);
    this.widget.remove();
    this.widget = null;
  }

  private showLoading (): void {
    const shell = this.ensureStatusShell();
    this.options.elements.status.dataset.state = 'loading';
    shell.loading.setAttribute('aria-hidden', 'false');
    shell.error.setAttribute('aria-hidden', 'true');
    this.options.elements.status.append(shell.root);
  }

  private clearStatus (): void {
    this.options.elements.status.textContent = '';
    this.options.elements.status.removeAttribute('data-state');
  }

  private showFailure (): void {
    const shell = this.ensureStatusShell();
    this.options.elements.status.dataset.state = 'error';
    shell.loading.setAttribute('aria-hidden', 'true');
    shell.error.setAttribute('aria-hidden', 'false');
    this.options.elements.status.append(shell.root);
  }

  private ensureStatusShell (): HelpAgentHostStatusShell {
    if (this.statusShell) {
      return this.statusShell;
    }

    const root = document.createElement('div');
    root.className = 'help-agent-host-shell';
    root.dataset.test = 'help-agent-host-shell';

    const loading = document.createElement('div');
    loading.className = 'help-agent-host-loading';
    loading.dataset.test = 'help-agent-host-loading';
    const loadingMark = document.createElement('span');
    loadingMark.setAttribute('aria-hidden', 'true');
    const loadingRing = document.createElement('span');
    loadingRing.className = 'help-agent-host-loading-ring';
    loadingRing.dataset.test = 'help-agent-host-loading-ring';
    const loadingTitle = document.createElement('div');
    loadingTitle.className = 'help-agent-host-status-title';
    loadingTitle.textContent = translate('help.ai.loading.title');
    const loadingMessage = document.createElement('div');
    loadingMessage.className = 'help-agent-host-status-message help-agent-host-loading-message';
    loadingMessage.textContent = translate('help.ai.loading.message');
    loadingMark.append(loadingRing);
    loading.append(loadingMark, loadingTitle, loadingMessage);

    const error = document.createElement('div');
    error.className = 'help-agent-host-error';
    error.dataset.test = 'help-agent-host-error';
    const errorIcon = document.createElement('span');
    errorIcon.className = 'help-agent-host-error-icon';
    errorIcon.setAttribute('aria-hidden', 'true');
    errorIcon.textContent = '!';
    const errorTitle = document.createElement('div');
    errorTitle.className = 'help-agent-host-status-title';
    errorTitle.textContent = translate('help.ai.unavailable.title');
    const errorMessage = document.createElement('div');
    errorMessage.className = 'help-agent-host-status-message help-agent-host-status-copy';
    errorMessage.textContent = translate('help.ai.unavailable.message');
    const retryMessage = document.createElement('div');
    retryMessage.className = 'help-agent-host-status-message help-agent-host-status-copy';
    retryMessage.textContent = translate('help.ai.retry.message');
    const retry = document.createElement('button');
    retry.className = 'help-agent-host-retry';
    retry.dataset.action = 'help-agent-retry';
    retry.type = 'button';
    retry.textContent = translate('help.ai.retry');
    retry.addEventListener('click', () => {
      this.retry().catch(() => this.showFailure());
    });
    error.append(errorIcon, errorTitle, errorMessage, retryMessage, retry);

    root.append(loading, error);
    loading.setAttribute('aria-hidden', 'true');
    error.setAttribute('aria-hidden', 'true');
    this.statusShell = { root, loading, error, ring: loadingRing };
    return this.statusShell;
  }

  private readonly onClose = (): void => {
    this.close();
    this.options.callbacks.close();
  };

  private readonly onExpandedChange = (): void => {
    this.options.callbacks.reposition();
  };

  private readonly onNotify = (event: Event): void => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (typeof detail !== 'object' || detail === null || !('message' in detail) || !('type' in detail)) {
      return;
    }
    const { message, type } = detail as { message: unknown, type: unknown };
    if (
      typeof message !== 'string'
      || message.trim().length === 0
      || (type !== 'info' && type !== 'success' && type !== 'error' && type !== 'warning')
    ) {
      return;
    }
    this.options.elements.status.textContent = message;
  };

  private readonly onAuthError = (event: Event): void => {
    const detail = (event as CustomEvent<unknown>).detail;
    if (typeof detail !== 'object' || detail === null || !('status' in detail)) {
      return;
    }
    const { status } = detail as { status: unknown };
    if (status !== 401 && status !== 403) {
      return;
    }
    this.disposeWidget();
    if (status === 401) {
      this.options.callbacks.logout();
      return;
    }
    this.options.elements.status.textContent = translate('help.ai.account.unavailable');
  };
}
